<script setup lang="ts">
import { computed } from 'vue';

import Divider from '~/components/shared/Divider.vue';
import InfoGridItem from '~/components/shared/InfoGridItem.vue';
import { GridSection } from '~/types/grid';

import usePartnerDetails from '../../queries/usePartnerDetails';

interface Props {
  partnerId?: null | number;
}

const props = defineProps<Props>();

const partnerIdRef = computed(() => props.partnerId ?? null);

const partnerQuery = usePartnerDetails(partnerIdRef);

const partnerData = computed(() => partnerQuery.data.value ?? null);

const partner = computed(() => partnerData.value?.partner ?? null);
const formattedDocument = computed(() => partnerData.value?.formattedDocument ?? '');
const blocklist = computed(() => partnerData.value?.blocklist ?? null);
const blocklistError = computed(() => partnerData.value?.blocklistError ?? false);

const isLoading = computed(() => partnerQuery.isLoading.value || partnerQuery.isFetching.value);
const hasPartnerId = computed(() => Boolean(partnerIdRef.value));

const blocklistLabel = computed(() => {
  if (blocklistError.value) return 'Sem dados';
  return blocklist.value?.blacklist_reason || 'Sem restrições';
});
const blocklistTagColor = computed<'danger' | 'success'>(() => (blocklist.value ? 'danger' : 'success'));

const mobilePhone = computed(() => partner.value?.responsible?.contact?.mobile || '');

const landlinePhone = computed(() => partner.value?.contact?.phone
  || partner.value?.responsible?.contact?.landline
  || '');

const email = computed(() => partner.value?.responsible?.contact?.email
  || partner.value?.contact?.email
  || '');

const profileLabel = computed(() => {
  const registrationType = partner.value?.registration_type;
  if (!registrationType) return '';

  return registrationType
    .split('_')
    .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
});

const levelLabel = computed(() => {
  const level = partner.value?.sfplus?.level;
  if (level === null || level === undefined) return '';
  if (level === 99) return 'Black';
  return level.toString();
});

const gridSections = computed<GridSection[]>(() => [
  {
    items: [
      { label: 'ID', value: partner.value?.id ?? null },
      { label: 'Nome', value: partner.value?.trade_name ?? '' },
      { label: 'Razão Social', value: partner.value?.corporate_name ?? '' },
      { label: 'CNPJ', value: formattedDocument.value },
      { label: 'Perfil', value: profileLabel.value },
      { label: 'Nível', value: levelLabel.value },
      {
        isTag: true,
        label: 'Lista de Bloqueios',
        tagColor: blocklistTagColor.value,
        value: blocklistLabel.value,
      },
    ],
    title: 'Empresa',
  },
  {
    items: [
      { label: 'Celular', value: mobilePhone.value },
      { label: 'Fixo', value: landlinePhone.value },
      { label: 'Email', value: email.value },
    ],
    title: 'Contato',
  },
]);
</script>

<template>
  <div class="col-12">
    <div class="partner-details card mb-4">
      <div class="card-body p-4">
        <h3 class="section-title mb-4">Dados do Parceiro</h3>

        <div v-if="!hasPartnerId" class="text-muted">
          Nenhum parceiro vinculado a este financiamento.
        </div>

        <div v-else-if="partnerQuery.isError.value" class="text-danger">
          Não foi possível carregar os dados do parceiro.
        </div>

        <div v-else-if="partner || isLoading" class="grid-sections">
          <template
            v-for="(section, index) in gridSections"
            :key="section.title"
          >
            <div class="grid-section">
              <h4 class="section-subtitle">{{ section.title }}</h4>
              <div class="datagrid">
                <InfoGridItem
                  v-for="item in section.items"
                  :key="`${section.title}-${item.label}`"
                  :label="item.label"
                  :is-tag="item.isTag"
                  :tag-color="item.tagColor"
                  :value="item.value"
                  :is-loading="isLoading"
                />
              </div>
            </div>
            <Divider
              v-if="index < gridSections.length - 1"
              orientation="horizontal"
            />
          </template>
        </div>

        <div v-else class="text-muted">
          Nenhuma informação do parceiro disponível.
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.partner-details {
  background: #fff;
  border: none;
  border-radius: 18px;
  box-shadow: 0 4px 24px 0 rgba(60, 72, 88, 0.08);

  .section-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
  }
}

.grid-sections {
  display: grid;
  gap: 1.5rem;
}

.grid-section {
  .section-subtitle {
    font-size: 1.2rem;
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 0.875rem;
  }

  .datagrid {
    display: grid;
    gap: 1rem;
  }
}
</style>
